<template>
  <div>
    <main class="mt-0">

      <div v-if="isLoading" class="saving-indicator">
        <span class="spinner"></span>데이터 로딩 중...
      </div>

      <div class="p-2">

        <!-- ── 페이지 헤더 ── -->
        <div class="d-flex align-items-center mb-3" style="flex-wrap:wrap; gap:.5rem;">
          <button class="btn btn-outline-secondary btn-sm" @click="$router.push('/Ma_RtxReserveInfoList')">
            <i class="bi bi-arrow-left"></i> 목록
          </button>
          <h3 class="rd-page-title mb-0"><u>호텔예약 상세</u></h3>
          <span class="ms-2" style="font-size:.85rem; color:#888;">{{ bookingNo }}</span>
        </div>

        <div class="row g-3">

          <!-- ════════════════════════════
            왼쪽 : 예약 정보
          ════════════════════════════ -->
          <div class="col-lg-8">

            <!-- ① 예약 개요 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-building"></i> 예약 개요
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">NTB 예약번호</span>
                    <span class="info-value fw-bold text-primary">{{ info.booking_no }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">RTX 예약코드</span>
                    <span class="info-value" style="color:#888; font-size:.82rem;">{{ info.booking_id }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">NTB 상태</span>
                    <span class="info-value">
                      <span :class="['status-badge', statusClass(info.status)]">
                        {{ statusText(info.status) }}
                      </span>
                      <!-- ✅ 취소 시 사유 표시 -->
                      <span v-if="info.status === 'bkg-cx' && info.cxl_flag"
                        style="font-size:.75rem; color:#e74c3c; font-weight:600; margin-left:.4rem;">
                        ({{ cxlLabel(info.cxl_flag) }})
                      </span>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">RTX 상태</span>
                    <span class="info-value">
                      <span v-if="rtxStatus"
                        :class="['status-badge', statusClass(rtxStatus)]"
                        :style="rtxStatus !== info.status ? 'outline:2px solid #e74c3c;' : ''">
                        {{ statusText(rtxStatus) }}{{ rtxStatus !== info.status ? ' ⚠️' : '' }}
                      </span>
                      <span v-else style="color:#ccc; font-size:11px;">-</span>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">공급사 참조번호</span>
                    <span class="info-value">{{ info.customer_ref || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Hotel Confirmation ID</span>
                    <span class="info-value fw-bold" style="color: crimson;">
                      {{ info.hotel_conf_id || '-' }}
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">예약일시</span>
                    <span class="info-value">{{ getKorDateFormat(info.created_at) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">최종수정</span>
                    <span class="info-value">{{ getKorDateFormat(info.updated_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ② 예약 정보 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-calendar-check"></i> 예약 정보
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item info-item--full">
                    <span class="info-label">호텔명</span>
                    <span class="info-value fw-bold">{{ info.hotel_name }}</span>
                  </div>
                  <div class="info-item info-item--full">
                    <span class="info-label">객실</span>
                    <span class="info-value">{{ info.room_desc || '-' }}</span>
                  </div>
                  <div class="info-item info-item--full" v-if="info.supplier_desc">
                    <span class="info-label">공급사 객실설명</span>
                    <span class="info-value" style="font-size:.8rem; color:#666;">
                      {{ info.supplier_desc }}
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">체크인</span>
                    <span class="info-value fw-bold text-primary">
                      {{ getDateFormat(info.check_in) }}
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">체크아웃</span>
                    <span class="info-value fw-bold text-primary">
                      {{ getDateFormat(info.check_out) }}
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">숙박/인원/객실</span>
                    <span class="info-value">
                      {{ info.night_count }}박 / 성인 {{ info.adult_count }}명 / {{ info.room_count }}실
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">식사</span>
                    <span class="info-value">{{ foodLabel(info.food_code) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">요금유형</span>
                    <span class="info-value">{{ info.rate_type || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">환불여부</span>
                    <span class="info-value">
                      <!-- 취소 완료된 건 -->
                      <span v-if="info.status === 'bkg-cx'"
                        style="color:#95a5a6; font-weight:600;">
                        취소완료
                      </span>
                      <!-- 진행 중인 건: 요금플랜 기준 표시 -->
                      <span v-else-if="info.non_refundable == 1"
                        style="color:#e74c3c; font-weight:600;">
                        환불불가
                      </span>
                      <span v-else
                        style="color:#27ae60; font-weight:600;">
                        환불가능
                      </span>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">금연여부</span>
                    <span class="info-value">
                      {{ info.non_smoking == 1 ? '금연' : info.non_smoking == 0 ? '흡연' : '-' }}
                    </span>
                  </div>
                  <div class="info-item info-item--full" v-if="info.special_request">
                    <span class="info-label">특별요청사항(호텔참고+보장X)</span>
                    <span class="info-value" style="color:#b45309;">{{ info.special_request }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ③ 투숙객 정보 -->
            <div class="detail-card" v-if="guests.length > 0">
              <div class="detail-card-header">
                <i class="bi bi-people"></i> 투숙객 정보
              </div>
              <div class="detail-card-body">
                <div v-for="(g, i) in guests" :key="i"
                  style="display:flex; align-items:center; gap:.8rem; padding:.5rem .8rem; background:#f8f9ff; border-radius:6px; border:1px solid #e2e8f0; margin-bottom:.5rem;">
                  <span style="font-weight:700; color:#1a6eb5; min-width:36px;">{{ g.room_idx }}실</span>
                  <span style="font-weight:600;">{{ g.salutation }} {{ g.first_name }} {{ g.last_name }}</span>
                  <span style="font-size:.75rem; color:#888;">({{ g.nationality }})</span>
                </div>
              </div>
            </div>

            <!-- ④ 예약자 정보 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-person-circle"></i> 예약자 정보
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">성명</span>
                    <span class="info-value">{{ info.contact_name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">연락처</span>
                    <span class="info-value">{{ info.contact_no }}</span>
                  </div>
                  <div class="info-item info-item--full">
                    <span class="info-label">이메일</span>
                    <span class="info-value">{{ info.contact_email || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">회원여부</span>
                    <span class="info-value">
                      <span v-if="info.mem_flag === 'Y'" style="color:#3498db; font-weight:600;">
                        회원 ({{ info.mem_id }})
                      </span>
                      <span v-else style="color:#aaa;">비회원</span>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">유료멤버십</span>
                    <span class="info-value">{{ info.memShip === 'N' ? '멤버십' : '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ⑤ 결제 정보 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-credit-card"></i> 결제 정보
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">판매가</span>
                    <span class="info-value fw-bold text-primary" style="font-size:1.05rem;">
                      {{ currency(info.selling_krw) }}원
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">원가</span>
                    <span class="info-value">{{ currency(info.cost_krw) }}원</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">마진</span>
                    <span class="info-value fw-bold"
                      :style="{ color: info.margin_krw > 0 ? '#27ae60' : '#e74c3c' }">
                      {{ currency(info.margin_krw) }}원
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">결제유형</span>
                    <span class="info-value">
                      {{ info.payType === 'fu' ? '전액' : info.payType === 'p1' ? '부분' : '-' }}
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">세금(USD)</span>
                    <span class="info-value">{{ info.tax_usd || 0 }} USD</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">리조트피(USD)</span>
                    <span class="info-value">{{ info.resort_fee_usd || 0 }} USD</span>
                  </div>
                  <div class="info-item" v-if="info.cancel_fee > 0">
                    <span class="info-label">취소수수료</span>
                    <span class="info-value fw-bold" style="color:#e74c3c;">
                      {{ currency(info.cancel_fee) }}원
                    </span>
                  </div>
                  <div class="info-item" v-if="info.cancel_date">
                    <span class="info-label">취소일시</span>
                    <span class="info-value">{{ getKorDateFormat(info.cancel_date) }}</span>
                  </div>

                  <!-- ✅ 취소사유 추가 -->
                  <div class="info-item" v-if="info.status === 'bkg-cx'">
                    <span class="info-label">취소사유</span>
                    <span class="info-value fw-bold" style="color:#e74c3c;">
                      {{ cxlLabel(info.cxl_flag) }}
                    </span>
                  </div>

                  <!-- 기존 취소처리자 -->
                  <div class="info-item" v-if="info.cxl_id">
                    <span class="info-label">취소처리자</span>
                    <span class="info-value">{{ info.cxl_id }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ⑥ 취소 정책 -->
            <div class="detail-card"
              v-if="info.cancel_remarks || cancelPolicyDetails.length > 0">
              <div class="detail-card-header">
                <i class="bi bi-exclamation-triangle"></i> 취소 정책
              </div>
              <div class="detail-card-body">
                <div v-if="info.cancel_remarks"
                  style="font-size:.82rem; color:#78350f; line-height:1.7; margin-bottom:.6rem; background:#fffbeb; border:1px solid #fde68a;border-radius:6px; padding:.7rem;">
                  {{ info.cancel_remarks }}
                </div>
                <div v-if="cancelPolicyDetails.length > 0">
                  <div v-for="(p, i) in cancelPolicyDetails" :key="i"
                    :style="{
                      display:'flex', justifyContent:'space-between', alignItems:'center',
                      gap:'.8rem', padding:'.45rem .7rem', borderRadius:'6px',
                      background: p.isCurrent ? '#fff3cd' : '#f9fafb',
                      border: p.isCurrent ? '1px solid #f5c344' : '1px solid #e2e8f0',
                      marginBottom:'.3rem', fontSize:'.8rem'
                    }">
                    <span style="color:#555;">
                      {{ p.label }}
                      <span v-if="p.isCurrent"
                        style="font-size:.7rem; font-weight:700; color:#b45309; margin-left:.4rem;">
                        (현재 적용)
                      </span>
                    </span>
                    <span :style="{ fontWeight:'700', color: p.percentColor }">
                      {{ p.percentText }}
                    </span>
                  </div>
                  <p style="font-size:.7rem; color:#aaa; margin:.5rem 0 0;">※ 시간은 UTC 기준입니다.</p>
                </div>
              </div>
            </div>

          </div>

          <!-- ════════════════════════════
               오른쪽 : 관리자 액션
          ════════════════════════════ -->
          <div class="col-lg-4">

            <!-- 담당자 확인 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-person-check"></i> 담당자 확인
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">확인자</span>
                    <span class="info-value fw-bold">{{ info.check_id || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">확인일시</span>
                    <span class="info-value">
                      {{ info.check_date ? getKorDateFormat(info.check_date) : '-' }}
                    </span>
                  </div>
                </div>
                <button class="action-btn action-btn--primary mt-2 w-100" @click="doCheckConfirm" :disabled="actionLoading.check">
                  <i class="bi bi-check-circle"></i>
                  {{ actionLoading.check ? '처리 중...' : (info.check_id ? '재확인처리' : '확인처리') }}
                </button>
              </div>
            </div>

            <!-- 관리자 액션 버튼 -->
            <div class="detail-card">
              <div class="detail-card-header">
                <i class="bi bi-gear"></i> 관리자 기능버튼
              </div>
              <div class="detail-card-body d-flex flex-column gap-2">

                <button class="action-btn action-btn--info w-100"
                  @click="syncRtxStatus" :disabled="actionLoading.rtx">
                  <i class="bi bi-arrow-repeat"></i>
                  {{ actionLoading.rtx ? '조회 중...' : 'RTX 상태 동기화' }}
                </button>

                <button class="action-btn action-btn--success w-100"
                  @click="resendMail(info.status === 'bkg-cx' ? 'cancel' : 'confirm')"
                  :disabled="actionLoading.mail || (info.status !== 'bkg-cf' && info.status !== 'bkg-cx')">
                  <i class="bi bi-envelope"></i>
                  {{ actionLoading.mail ? '발송 중...'
                    : info.status === 'bkg-cx' ? '취소메일 재발송'
                    : '확정메일 재발송' }}
                </button>

                <!-- <button class="action-btn action-btn--success w-100"
                  @click="resendMail('confirm')"
                  :disabled="actionLoading.mail || info.status !== 'bkg-cf'">
                  <i class="bi bi-envelope"></i>
                  {{ actionLoading.mail ? '발송 중...' : '확정메일 재발송' }}
                </button>

                <button class="action-btn action-btn--secondary w-100"
                  @click="resendMail('cancel')"
                  :disabled="actionLoading.mail || info.status !== 'bkg-cx'">
                  <i class="bi bi-envelope-x"></i>
                  {{ actionLoading.mail ? '발송 중...' : '취소메일 재발송' }}
                </button> -->

                <button class="action-btn action-btn--warning w-100"
                  @click="resendKakao" :disabled="actionLoading.kakao">
                  <i class="bi bi-chat-dots"></i>
                  {{ actionLoading.kakao ? '발송 중...' : '알림톡 재발송' }}
                </button>

                <hr style="margin:.2rem 0;">

                <button class="action-btn action-btn--danger w-100"
                  @click="forceCancelHotel" :disabled="actionLoading.cancel || info.status !== 'bkg-cf'">
                  <i class="bi bi-x-circle"></i>
                  {{ actionLoading.cancel ? '처리 중...' : '예약 강제 취소' }}
                </button>

              </div>
            </div>

            <!-- 메일 발송 이력 -->
            <div class="detail-card" v-if="mailHistory.length > 0">
              <div class="detail-card-header">
                <i class="bi bi-clock-history"></i> 메일발송이력(수신자)
              </div>
              <div class="detail-card-body">
                <div v-for="(m, i) in mailHistory" :key="i"
                  style="display:flex; justify-content:space-between; align-items:center; padding:.35rem 0; border-bottom:1px dashed #e2e8f0; font-size:.75rem;">
                  <span>
                    <span :style="{
                      color: m.mailFlag==='CO' ? '#27ae60' : m.mailFlag==='CX' ? '#e74c3c' : '#e67e22',
                      fontWeight:'600'
                    }">
                      {{ m.mailFlag === 'CO' ? '확정' : m.mailFlag === 'CX' ? '취소' : m.mailFlag === 'CI' ? '체크인' : '기타' }}
                    </span>
                    &nbsp;{{ m.eMail }}
                  </span>
                  <span style="color:#aaa; white-space:nowrap; margin-left:.5rem;">
                    {{ m.sendDate ? this.getKorDateFormat(m.sendDate) : '-' }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- ⓪ 호텔 기본 정보 -->
            <div class="detail-card" v-if="hotelDetailInfo.hotel_id">
              <div class="detail-card-header">
                <i class="bi bi-building-check"></i> 호텔 정보
              </div>
              <div class="detail-card-body">
                <div class="info-grid">
                  <div class="info-item info-item--full">
                    <span class="info-label">호텔명</span>
                    <span class="info-value fw-bold">
                      {{ hotelDetailInfo.hotel_name }}
                      <span v-if="hotelDetailInfo.original_name"
                        style="font-size:.78rem; color:#888; font-weight:400; margin-left:.4rem;">
                        ({{ hotelDetailInfo.original_name }})
                      </span>
                    </span>
                  </div>
                  <div class="info-item info-item--full">
                    <span class="info-label">위치</span>
                    <span class="info-value">
                      {{ hotelDetailInfo.address }}, {{ hotelDetailInfo.city }}, {{ hotelDetailInfo.state_province }}
                      <!-- {{ [hotelDetailInfo.address, hotelDetailInfo.city, hotelDetailInfo.country]
                        .filter(Boolean).join(', ') || '-' }} -->
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">연락처</span>
                    <span class="info-value">{{ hotelDetailInfo.phone || '-' }}</span>
                  </div>
                  <!-- <div class="info-item">
                    <span class="info-label">이메일</span>
                    <span class="info-value">{{ hotelDetailInfo.email || '-' }}</span>
                  </div> -->
                  <div class="info-item" v-if="hotelDetailInfo.website">
                    <span class="info-label">웹사이트</span>
                    <a :href="hotelDetailInfo.website" target="_blank"
                      style="font-size:.82rem; color:#1a6eb5;">
                      {{ hotelDetailInfo.website }}
                    </a>
                  </div>
                  <div class="info-item">
                    <span class="info-label">체크인 | 체크아웃</span>
                    <span class="info-value">
                      {{ hotelDetailInfo.check_in_time || '-' }}&nbsp;&nbsp;|&nbsp;&nbsp;{{ hotelDetailInfo.check_out_time || '-' }}
                    </span>
                  </div>
                  <!-- 호텔 설명: 우측 컬럼이 좁으니 3줄로 제한 -->
                  <div class="info-item info-item--full" v-if="hotelDetailInfo.description">
                    <span class="info-label">호텔 설명</span>
                    <span class="info-value" style="
                      font-size:.78rem; color:#444; line-height:1.6;
                      display:-webkit-box; -webkit-line-clamp:4;
                      -webkit-box-orient:vertical; overflow:hidden;
                    ">{{ hotelDetailInfo.description }}</span>
                  </div>
                  <div class="info-item info-item--full" v-if="hotelFacilities.length > 0">
                    <span class="info-label">서비스 / 시설</span>
                    <div style="display:flex; flex-wrap:wrap; gap:.3rem; margin-top:.3rem;">
                      <span v-for="(f, i) in hotelFacilities" :key="i"
                        style="font-size:.72rem; background:#e8f4ea; color:#2d6a4f;
                              padding:2px 8px; border-radius:12px; border:1px solid #b7dfbf;">
                        {{ f }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 하단 버튼 -->
        <div class="d-flex justify-content-center gap-2 mt-3 pb-4">
          <button class="btn btn-outline-secondary"
            @click="$router.push('/Ma_RtxReserveInfoList')">
            <i class="bi bi-list"></i> 목록으로
          </button>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import { handlerInfo } from '../assets/js/commonFunctions'

export default {
  data() {
    return {
      isLoading: false,
      bookingNo: '',
      info:        {},
      guests:      [],
      mailHistory: [],
      rtxStatus:   null,

      actionLoading: {
        check:  false,
        rtx:    false,
        mail:   false,
        kakao:  false,
        cancel: false,
      },

      hotelDetailInfo: {},   // 호텔기본정보

      handTel: [],      
    }
  },

  computed: {
    user() { return this.$store.state.user },

    hotelFacilities() {
      const raw = this.hotelDetailInfo.facilities
      if (!raw) return []
      try 
      {
        const list = typeof raw === 'string' ? JSON.parse(raw) : raw
        // facilities는 [{code, description}] 또는 [code] 형태
        return Array.isArray(list)
          ? list.map(f => f.description || f).filter(Boolean).slice(0, 15)
          : []
      } 
      catch (e) 
      { return [] }
    },

    cancelPolicyDetails() {
      const raw = this.info.cancel_policy
      if (!raw) return []

      let policy
      try { policy = typeof raw === 'string' ? JSON.parse(raw) : raw }
      catch (e) { return [] }
      
      const policies = policy.cancellation_policies || []
      if (policies.length === 0) return []
      const now = new Date()
      return policies.map(p => {
        const from    = p.date_from ? new Date(p.date_from) : null
        const to      = p.date_to   ? new Date(p.date_to)   : null
        const percent = p.penalty_percentage || 0
        
        let percentText = '', percentColor = '#16a34a'
        if      (percent === 0)  { percentText = '무료취소';        percentColor = '#16a34a' }
        else if (percent >= 100) { percentText = '환불불가 (100%)'; percentColor = '#c62828' }
        else                     { percentText = `${percent}% 수수료`; percentColor = '#b45309' }
        const isCurrent = !!(from && now >= from && (!to || now < to))
        return {
          label: `${from ? this.getKorDateFormat(from) : '-'} ~ ${to ? this.getKorDateFormat(to) : '-'}`,
          percentText, percentColor, isCurrent
        }
      })
    }
  },

  created() {
    this.bookingNo = this.$route.query.bookingNo || ''
  },

  mounted() {
    if (this.user.handlerGrade >= 20 || this.user.handlerGrade == undefined) 
    {
      const swal = require('sweetalert2')
      swal.fire({ title: '로그인 필요', text: '접근권한 확인불가.', icon: 'error' })
      this.$router.push({ path: '/' })
      return
    }
    if (this.user.handlerId != undefined) 
    {
      document.getElementById('admin').style.display = 'block'
    }
    document.getElementById('user').style.display = 'none'

    this.loadDetail()
  },

  methods: {
    getDateFormat(date)    { return this.$dateFormat(date) },
    getKorDateFormat(date) { return this.$korDateFormat(date) },
    currency(val)          { return this.$currencyFormat(val) },

    statusText(s) {
      const m = { 'bkg-ip':'처리중','bkg-cf':'확정','bkg-ps':'공급사대기','bkg-af':'실패','bkg-cx':'취소' }
      return m[s] || s || '-'
    },
    statusClass(s) {
      const m = { 'bkg-ip':'status-ip','bkg-cf':'status-cf','bkg-ps':'status-ps','bkg-af':'status-af','bkg-cx':'status-cx' }
      return m[s] || ''
    },
    foodLabel(code) {
      const m = { 1:'룸온리',2:'조식포함',3:'점심포함',4:'저녁포함',5:'하프보드',6:'풀보드',7:'올인클루시브' }
      return code ? (m[code] || '-') : '-'
    },

    // ── 데이터 로드 ──────────────────────────────────────────
    async loadDetail() {
      if (!this.bookingNo) return
      this.isLoading = true
      try 
      {
        // 예약 마스터
        const info = await this.$api('/api/hotelBookingDetail', { param: [this.bookingNo] })
        if (info && info.length > 0) this.info = info[0]

        // 예약 마스터 로드 후, hotel_id 확보된 시점에 추가
        const hotelDetail = await this.$api('/api/hotelDetailForBooking', {
          param: [this.info.hotel_id]
        })
        if (hotelDetail && hotelDetail.length > 0) this.hotelDetailInfo = hotelDetail[0]

        // 투숙객
        const guests = await this.$api('/api/hotelBookingGuests', { param: [this.bookingNo] })
        if (guests && guests.length > 0) this.guests = guests

        // 메일 이력
        const hist = await this.$api('/api/getHotelMailHistory', { param: [this.bookingNo] })
        if (hist && hist.length > 0) this.mailHistory = hist

        // RTX 상태 자동 조회 (진입 시)
        await this.syncRtxStatus()
      } 
      catch (e) 
      {
        console.error('[detail] 로드 오류:', e)
      } 
      finally 
      {
        this.isLoading = false
        await this.getHandlerInfo()
      }
    },

    async getHandlerInfo() {
      let handTel = await handlerInfo(this.$api);
      if(handTel.length > 0)
      {
        this.handTel = handTel
      }
      // console.log("handTel: ", this.handTel)
    },

    // 취소사유
    cxlLabel(code) {
      const map = {
        'C0': '고객요청',
        'C1': '마감',
        'C2': '인원부족',
        'C3': '현지사정',
        'C4': '천재지변',
        'C5': '오류',
        'C6': '기타'
      }
      return code ? `${code} · ${map[code] || '사유불명'}` : '-'
    },

    // ── RTX 상태 동기화 ──────────────────────────────────────
    async syncRtxStatus() {
      // 예약저장실패 등으로 booking_id가 없는 경우, RTX 조회 불가
      if (!this.info.booking_id) return

      this.actionLoading.rtx = true
      try 
      {
        const res = await this.$api('/api/rtx/getBookStatus', {
          param: [{ booking_id: this.info.booking_id }]
        })
        this.rtxStatus  = res.status || null

        // // DB 상태도 동기화된 값으로 반영
        // if (res.status) this.info.status = res.status

        // ✅ 불일치 발견 시 경고 후 DB 갱신 여부 확인
        if (res.status && res.status !== this.info.status) {
          await this.$swal.fire({
            icon: 'warning',
            title: '상태 불일치 감지',
            html: `NTB: <b>${this.statusText(this.info.status)}</b> 
                  → RTX: <b>${this.statusText(res.status)}</b><br>
                  <small>RTX 기준으로 DB가 자동 보정되었습니다.</small>`,
            confirmButtonText: '확인'
          })
          this.info.status = res.status  // 경고 확인 후 반영
        } else if (res.status) {
          this.info.status = res.status
        }
      } 
      catch (e) 
      {
        console.warn('[RTX상태] 조회 실패:', e.message)
      } 
      finally 
      {
        this.actionLoading.rtx = false
      }
    },

    // ── 확인처리 ─────────────────────────────────────────────
    async doCheckConfirm() {
      this.actionLoading.check = true
      try 
      {
        let result = await this.$api('/api/updateHotelCheckInfo', {
          param: [ this.user.handlerId, this.bookingNo ]
        })

        if(result.affectedRows > 0) 
        {
          this.info.check_id   = this.user.handlerId
          this.info.check_date = new Date().toISOString()
          this.$swal.fire('', '확인처리 완료', 'success')
        }
        else 
        {
          this.$swal.fire('오류', '확인처리 실패', 'error')
        }

      } 
      catch (e) 
      {
        this.$swal.fire('오류', '확인처리 실패', 'error')
      } 
      finally 
      {
        this.actionLoading.check = false
      }
    },

    // ── 메일 재발송 ──────────────────────────────────────────
    async resendMail(type) {
      const label = type === 'confirm' ? '확정' : '취소'
      const result = await this.$swal.fire({
        title: `${label}메일 재발송`,
        html: `<b>${this.info.contact_email}</b> 으로 재발송합니다.`,
        showCancelButton: true,
        confirmButtonText: '발송',
        cancelButtonText:  '닫기',
      })
      if (!result.isConfirmed) return

      this.actionLoading.mail = true
      try 
      {
        await this.$api('/api/hotel/resendMail', {
          param: [{ bookingId: this.info.booking_id, type }]
        })
        this.$swal.fire('', `${label}메일 재발송 완료`, 'success')
        // 이력 갱신
        const hist = await this.$api('/api/getHotelMailHistory', { param: [this.bookingNo] })
        if (hist && hist.length > 0) this.mailHistory = hist
      } 
      catch (e) 
      {
        this.$swal.fire('오류', '메일 발송 실패', 'error')
      } 
      finally 
      {
        this.actionLoading.mail = false
      }
    },

    // ── 알림톡 재발송 ────────────────────────────────────────
    async resendKakao() {
      const result = await this.$swal.fire({
        title: '알림톡 재발송',
        html: `<b>${this.info.contact_no}</b> 으로 재발송합니다.`,
        showCancelButton: true,
        confirmButtonText: '발송',
        cancelButtonText: '닫기',
      })
      if (!result.isConfirmed) return

      this.actionLoading.kakao = true
      try 
      {
        const kakaoAlimData = {
          alimFlag    : this.info.status === 'bkg-cx' ? 'CX' : 'CO',
          clientCall  : this.info.contact_no,
          clientName  : this.info.contact_name,
          rtxName     : this.info.hotel_name,
          resName     : this.info.hotel_name,
          resPaxName  : this.info.contact_name,
          resPaxCnt   : `대인: ${this.info.adult_count || 0} / 소인: 0`,
          resRoom     : this.info.room_count,
          resStayDate : `${this.getDateFormat(this.info.check_in)} ~ ${this.getDateFormat(this.info.check_out)}`,
          resPhone    : this.info.contact_no,
          resCode     : this.bookingNo,
          resDate     : this.info.cancel_date ? String(this.info.cancel_date).slice(0, 10) : new Date().toISOString().slice(0, 10),  // cancel_date 없을 때 폴백
          resPayAmount: this.currency(this.info.selling_krw) + '원',
          resFeeAmount: this.currency(this.info.cancel_fee || 0) + '원',
          memFlag     : this.info.mem_flag || 'N',
          re_send     : 'Y',
          re_text     : '',
          indexCode   : undefined,
          rtxPhone    : '',
        }
        await this.$api('api/kakaoAlimSend10', { param: [{ kakaoAlimData }] })
        this.$swal.fire('', '알림톡 재발송 완료', 'success')
      } 
      catch (e) 
      {
        this.$swal.fire('오류', '알림톡 발송 실패', 'error')
      } 
      finally 
      {
        this.actionLoading.kakao = false
      }
    },

    // ── 강제 취소 ────────────────────────────────────────────
    async forceCancelHotel() {
      const { value: cxlFlag, isConfirmed } = await this.$swal.fire({
        title: '⚠️ 예약 강제 취소',
        html: `<div class="text-start" style="font-size:.88rem;">
          <p class="text-danger fw-bold">관리자 취소입니다. RTX + Toss 환불이 진행됩니다.</p>
          <hr>
          <p>예약번호: <b>${this.bookingNo}</b></p>
          <p>판매가: <b>${this.currency(this.info.selling_krw)}원</b> → 전액 환불</p>
          <hr>
          <label style="font-weight:600; margin-bottom:.4rem; display:block;">취소사유 선택 <span class="text-danger">*</span></label>
          <select id="cxl-flag-select" class="form-select form-select-sm">
            <option value="">-- 선택 --</option>
            <option value="C0">C0 · 고객요청</option>
            <option value="C1">C1 · 마감</option>
            <option value="C2">C2 · 인원부족</option>
            <option value="C3">C3 · 현지사정</option>
            <option value="C4">C4 · 천재지변</option>
            <option value="C5">C5 · 오류</option>
            <option value="C6">C6 · 기타</option>
          </select>
        </div>`,
        showCancelButton: true,
        confirmButtonText: '취소 진행',
        cancelButtonText:  '돌아가기',
        confirmButtonColor: '#c62828',
        preConfirm: () => {
          const val = document.getElementById('cxl-flag-select').value
          if (!val) {
            this.$swal.showValidationMessage('취소사유를 선택해주세요.')
            return false
          }
          return val
        }
      })

      if (!isConfirmed) return

 // ② 수수료 조회 (마이페이지 패턴 동일)
 this.actionLoading.cancel = true
  let chargeInfo = null
  try 
  {
    const res = await axios.post('/api/hotel/getCancelCharge', {
      bookingNo: this.bookingNo
    })
    chargeInfo = res.data
  } 
  catch (e) 
  {
    this.actionLoading.cancel = false
    this.$swal.fire('오류', '수수료 조회 실패', 'error')
    return
  }
  this.actionLoading.cancel = false

  const cancelFee = chargeInfo?.chargeKrw || 0
  const totalKrw  = this.info.selling_krw || 0
  const refundAmt = totalKrw - cancelFee   // ← 여기서 계산

  // ③ 수수료 안내 후 최종 확인
  const confirm2 = await this.$swal.fire({
    title: '⚠️ 예약 강제 취소',
    html: `<div class="text-start" style="font-size:.88rem;">
      <p class="text-danger fw-bold">관리자 취소입니다. RTX + Toss 환불이 진행됩니다.</p>
      <hr>
      <table style="width:100%; border-collapse:collapse;">
        <tr><td>판매가</td>
            <td class="text-end fw-bold">${this.currency(totalKrw)}원</td></tr>
        <tr><td>취소수수료</td>
            <td class="text-end fw-bold" style="color:#c62828;">
              ${cancelFee > 0 ? `- ${this.currency(cancelFee)}원` : '없음'}
            </td></tr>
        <tr style="border-top:1px solid #e2e8f0;">
          <td class="fw-bold">환불예정액</td>
          <td class="text-end fw-bold" style="color:#1565c0;">
            ${this.currency(refundAmt)}원
          </td>
        </tr>
      </table>
    </div>`,
    showCancelButton: true,
    confirmButtonText: '취소 진행',
    cancelButtonText:  '돌아가기',
    confirmButtonColor: '#c62828',
  })
  if (!confirm2.isConfirmed) return

      // ④ 실제 취소 실행
      this.actionLoading.cancel = true
      try 
      {
        const res = await axios.post('/api/hotel/cancelBooking', {
          bookingNo: this.bookingNo,
          cancelFee,             // ← 실제 수수료
          cxlId    : this.user.handlerId,
          cxlFlag,
        })
        if (res.data.success) 
        {
          this.info.status    = 'bkg-cx'
          this.info.cxl_flag  = cxlFlag
          this.info.cxl_id    = this.user.handlerId
          this.info.cancel_fee = cancelFee

          this.sendKakaoHotelCancel(cancelFee, refundAmt)  // ← 정확한 금액 전달

          this.$swal.fire('취소 완료', '예약이 취소되었습니다.', 'success')
        } 
        else 
        {
          this.$swal.fire('오류', res.data.message || '취소 실패', 'error')
        }
      } 
      catch (e) 
      {
        this.$swal.fire('오류', '취소 처리 실패', 'error')
      } 
      finally 
      {
        this.actionLoading.cancel = false
      }
    },

    sendKakaoHotelCancel(cancelFee = 0, refundAmt = 0) {
      const sendTargets = []
      this.handTel.forEach(h => {
        sendTargets.push({ callNo: h.aimCall, callName: h.handlerNameKor ?? '담당자' })
      })
      sendTargets.push({
        callNo  : this.info.contact_no   || '',
        callName: this.info.contact_name || ''
      })

      const stayDateRange = `${this.getDateFormat(this.info.check_in)} ~ ${this.getDateFormat(this.info.check_out)}`
      const paxCnt        = `대인: ${this.info.adult_count || 0} / 소인: 0`
      const today         = new Date().toISOString().slice(0, 10)

      const promises = sendTargets.map(target => {
        const kakaoAlimData = {
          alimFlag    : 'CX',
          clientCall  : target.callNo,
          clientName  : target.callName,
          rtxName     : this.info.hotel_name,
          resName     : this.info.hotel_name,
          resPaxName  : this.info.contact_name,
          resPaxCnt   : paxCnt,
          resRoom     : this.info.room_count,
          resStayDate : stayDateRange,
          resPhone    : this.info.contact_no,
          resCode     : this.bookingNo,
          resDate     : today,
          resPayAmount: this.currency(refundAmt) + '원',
          resFeeAmount: this.currency(cancelFee) + '원',
          memFlag     : this.info.mem_flag || 'N',
        }
        return this.$api('api/kakaoAlimSend10', { param: [{ kakaoAlimData }] })
          .catch(e => console.error('[호텔 취소 알림톡 실패]', target.callNo, e))
      })
      Promise.all(promises)
    },
  }
}
</script>

<style scoped>
/* ── 로딩 인디케이터 ── */
.saving-indicator {
  position: fixed; top: 30%; left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999; background: rgba(255,255,255,.95);
  border-radius: 10px; padding: 2rem 2.5rem;
  box-shadow: 0 2px 16px rgba(0,0,0,.1);
  display: flex; align-items: center;
  font-size: 1.3rem; font-weight: bold; color: #007bff;
}
.spinner {
  width: 28px; height: 28px;
  border: 4px solid #e0e0e0; border-top: 4px solid #007bff;
  border-radius: 50%; margin-right: 1rem;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.rd-page-title {
  font-size: 1.6rem; font-weight: 700;
  color: var(--charcoal); margin: 0 0 .4rem; padding-left: 10px;
}

/* ── 카드 ── */
.detail-card {
  background: #fff; border: 1px solid #ddefd4;
  border-radius: 12px; box-shadow: 0 2px 12px rgba(90,160,51,.08);
  margin-bottom: 1rem; overflow: hidden;
}
.detail-card-header {
  display: flex; align-items: center; gap: .5rem;
  background: #2b3a2b; color: #fff;
  padding: .7rem 1.2rem; font-size: .9rem; font-weight: 600;
}
.detail-card-header i { color: #d4f0b8; font-size: 1rem; }
.detail-card-body { padding: 1.2rem; }

/* ── 정보 그리드 ── */
.info-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: .7rem;
}
.info-item {
  display: flex; flex-direction: column; gap: .2rem;
  padding: .65rem .8rem;
  background: #f0fbe7; border-radius: 7px; border: 1px solid #ddefd4;
}
.info-item--full { grid-column: 1 / -1; }
.info-label {
  font-size: .68rem; font-weight: 600; color: #5a7a5a;
  text-transform: uppercase; letter-spacing: .04em;
}
.info-value { font-size: .87rem; color: #1a2e1a; font-weight: 500; }
@media (max-width: 767px) { .info-grid { grid-template-columns: 1fr; } }

/* ── 상태 배지 ── */
.status-badge {
  display: inline-block; font-size: 11px; font-weight: 600;
  padding: 2px 8px; border-radius: 4px; color: #fff;
}
.status-ip { background: #3498db; }
.status-cf { background: #27ae60; }
.status-ps { background: #e67e22; }
.status-af { background: #e74c3c; }
.status-cx { background: #95a5a6; }

/* ── 액션 버튼 ── */
.action-btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: .4rem; padding: .5rem 1rem; border-radius: 7px;
  font-size: .83rem; font-weight: 600; border: none; cursor: pointer;
  transition: opacity .15s, transform .1s;
}
.action-btn:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
.action-btn:disabled { opacity: .45; cursor: not-allowed; }
.action-btn--primary   { background: #1a6eb5; color: #fff; }
.action-btn--info      { background: #3498db; color: #fff; }
.action-btn--success   { background: #27ae60; color: #fff; }
.action-btn--secondary { background: #7f8c8d; color: #fff; }
.action-btn--warning   { background: #e67e22; color: #fff; }
.action-btn--danger    { background: #c62828; color: #fff; }
</style>